// pages/ProfilePage.jsx
import { useEffect, useMemo, useState } from "react";
import { getOrder, userOrders } from "../api/apis";
import { useNavigate, useParams } from "react-router";
import { useApp } from "../Context";

export default function ProfilePage() {
    const size = 5;
    const navigate = useNavigate();
    const { restaurant } = useParams();
    const { auth, setAuth, orderids, setOrderids } = useApp();

    const [remoteOrderIds, setRemoteOrderIds] = useState([]);
    const [orders, setOrders] = useState({}); // changed to object
    const [remove, setRemove] = useState({});
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const loadRemoteOrders = async () => {
            if (auth) {
                const fetched = await fetchOrderIds(auth.phone, auth.code);
                setRemoteOrderIds(fetched);
            } else {
                setRemoteOrderIds([]);
            }
        };
        loadRemoteOrders();
    }, [auth, restaurant]);

    const fetchOrderIds = async (phone, code) => {
        if (phone && code) {
            const res = await userOrders(phone, code);
            if (res.ok) {
                return res.orders
                    .map((item) =>
                        typeof item === "object" && item !== null ? Number(item.id) : Number(item)
                    )
                    .filter((n) => !isNaN(n));
            }
        }
        return [];
    };

    const combinedOrderIds = useMemo(() => {
        const localIds = orderids.map((item) => item.id);
        return [...new Set([...localIds, ...remoteOrderIds])].filter((n) => !isNaN(n));
    }, [orderids, remoteOrderIds]);

    const fetchOrders = async () => {
        const bottom = size * (page - 1);
        const top = size * page;

        setLoading(true);
        try {
            let newLocals = [...orderids];
            let changed = false;
            const updatedOrders = { ...orders };

            for (let i = bottom; i < combinedOrderIds.length && i < top; i++) {
                const id = combinedOrderIds[i];
                if (updatedOrders[id]) continue;

                const result = await getOrder(id);
                updatedOrders[id] = result;

                const paid = result?.order?.order00?.paid === "1";
                const existing = newLocals.find((o) => o.id === id);

                if (!existing) {
                    newLocals.push({ id, paid });
                    changed = true;
                } else if (existing.paid !== paid) {
                    newLocals = newLocals.map((o) =>
                        o.id === id ? { ...o, paid } : o
                    );
                    changed = true;
                }
            }

            setOrders(updatedOrders);
            if (changed) {
                setOrderids(newLocals);
            }
        } catch (error) {
            console.error("Error fetching orders", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, [page, combinedOrderIds]);

    const handleCheck = (e) => {
        const id = e.target.name;
        setRemove((prev) =>
            e.target.checked
                ? { ...prev, [id]: true }
                : (() => {
                    const copy = { ...prev };
                    delete copy[id];
                    return copy;
                })()
        );
    };

    const handleRemove = () => {
        const toRemoveIds = new Set(Object.keys(remove).map(Number));
        const filtered = orderids.filter((item) => !toRemoveIds.has(item.id));
        setOrderids(filtered);
        setRemove({});
    };

    function OrderItem({ order, id }) {
        const local = orderids.find((o) => o.id === Number(id));
        const isLocal = Boolean(local);
        const isChecked = !!remove[id];

        if (!order) {
            return (
                <div
                    key={id}
                    className="border-bottom border-muted d-flex align-items-center justify-content-between"
                >
                    <p className="mb-0 p-2 fw-bold">#{id}</p>
                    <div className="spinner-border text-primary mx-4"></div>
                </div>
            );
        }

        if (!order.order || Object.keys(order.order).length === 0) {
            return (
                <div
                    key={id}
                    className="border-bottom border-muted d-flex align-items-center justify-content-between"
                >
                    <div className="d-flex align-items-center">
                        <i className="bi bi-exclamation-diamond text-danger fs-5"></i>
                        <p className="mb-0 p-2 fw-bold">#{id}</p>
                    </div>
                    <p className="mb-0 p-2 text-danger fw-semibold mx-3">
                        Ordern hittades inte
                    </p>
                </div>
            );
        }

        const { datum, orderid, belopp, paid } = order.order.order00;
        const paidBool = paid === "1";

        return (
            <div
                key={id}
                className="border-bottom border-muted d-flex align-items-center justify-content-between"
            >
                <div className="d-flex align-items-center">
                    {isLocal && (
                        <input
                            className="form-check-input my-0 me-3"
                            type="checkbox"
                            name={String(id)}
                            checked={isChecked}
                            onChange={handleCheck}
                        />
                    )}
                    {paidBool ? (
                        <i className="bi bi-check-circle text-success fs-5"></i>
                    ) : (
                        <i className="bi bi-exclamation-triangle text-primary fs-5"></i>
                    )}
                    <p className="mb-0 p-2 fw-bold">#{parseInt(orderid)}</p>
                    <p className="mb-0 p-2">
                        {`${datum.slice(0, 4)}-${datum.slice(4, 6)}-${datum.slice(6, 8)}`}
                    </p>
                </div>
                <div className="d-flex gap-1">
                    <p className="mb-0 p-2 fw-bold">{(belopp / 100).toFixed(2)} SEK</p>
                    <span
                        style={{ marginLeft: "auto" }}
                        onClick={() => navigate(`/${restaurant}/order/${orderid}`)}
                    >
            <i className="bi bi-arrow-right-short fs-2"></i>
          </span>
                </div>
            </div>
        );
    }

    function PageSwitcher() {
        const pageCount = Math.ceil(combinedOrderIds.length / size);
        if (pageCount <= 1) return null;

        return (
            <div className="d-flex justify-content-center mt-2">
                {Array.from({ length: pageCount }).map((_, i) => (
                    <button
                        key={i}
                        onClick={() => setPage(i + 1)}
                        className={`btn btn-sm me-1 ${
                            page === i + 1 ? "btn-secondary secondaryText" : "btn-outline-secondary"
                        }`}
                    >
                        {i + 1}
                    </button>
                ))}
            </div>
        );
    }

    const bottom = size * (page - 1);
    const top = size * page;

    return (
        <div className="mt-5 px-1">
            <div
                className="fs-5 fw-semibold d-flex align-items-center"
                onClick={() => navigate("../order")}
            >
                <i className="bi bi-arrow-left-short fs-1 fw-bold"></i>
                <p className="mb-0">Tillbaka</p>
            </div>
            <div className="px-2">
                <div className="d-flex align-items-center justify-content-between">
                    <p className="mt-1 text-muted fs-5 fw-semibold mb-0">Beställningar</p>
                    <button
                        className="btn btn-secondary secondaryText"
                        disabled={Object.keys(remove).length === 0}
                        onClick={handleRemove}
                    >
                        Ta bort markerade
                    </button>
                </div>
                <div className="card border-0 shadow-sm p-2 mt-1">
                    {combinedOrderIds.length > 0 ? (
                        combinedOrderIds.slice(bottom, top).map((id) => (
                            <OrderItem order={orders[id]} id={id} key={id} />
                        ))
                    ) : (
                        <div className="text-center text-muted">
                            Inga tidigare beställningar
                        </div>
                    )}
                    <PageSwitcher />
                </div>
                <div className="card p-3 mt-3">
                    {auth ? (
                        <button
                            className="btn btn-outline-danger"
                            onClick={() => {
                                navigate(`/${restaurant}/order`);
                                setTimeout(() => setAuth(null), 100);
                            }}
                        >
                            Logga ut
                        </button>
                    ) : (
                        <>
                            <p className="text-center fw-semibold mb-2">
                                Logga in för att se fler ordrar
                            </p>
                            <button
                                className="btn btn-primary primaryText"
                                onClick={() => navigate("login")}
                            >
                                Logga in
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
